// Push Notification Service for PRSNL CRM
const API_URL = process.env.REACT_APP_BACKEND_URL;

class PushNotificationService {
  constructor() {
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
    this.subscription = null;
    this.registration = null;
  }

  async init() {
    if (!this.isSupported) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      // Register service worker
      this.registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', this.registration);
      
      // Check existing subscription
      this.subscription = await this.registration.pushManager.getSubscription();
      
      return true;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return false;
    }
  }

  async requestPermission() {
    if (!this.isSupported) return 'unsupported';
    
    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  }

  async subscribe(memberInfo = {}) {
    if (!this.isSupported || !this.registration) {
      return { success: false, error: 'Not supported or not initialized' };
    }

    try {
      // Get VAPID public key from server
      const vapidResponse = await fetch(`${API_URL}/api/push/vapid-public-key`);
      const { publicKey } = await vapidResponse.json();
      
      // Convert VAPID key to Uint8Array
      const applicationServerKey = urlBase64ToUint8Array(publicKey);
      
      // Subscribe to push notifications
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });
      
      // Send subscription to server
      const response = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          endpoint: this.subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(this.subscription.getKey('p256dh')),
            auth: arrayBufferToBase64(this.subscription.getKey('auth'))
          },
          member_id: memberInfo.id || null,
          member_name: memberInfo.name || null,
          role: memberInfo.role || null
        })
      });
      
      const data = await response.json();
      console.log('Push subscription saved:', data);
      
      return { success: true, subscription: this.subscription };
    } catch (error) {
      console.error('Push subscription failed:', error);
      return { success: false, error: error.message };
    }
  }

  async unsubscribe() {
    if (!this.subscription) return { success: true };
    
    try {
      await this.subscription.unsubscribe();
      
      // Notify server
      await fetch(`${API_URL}/api/push/unsubscribe?endpoint=${encodeURIComponent(this.subscription.endpoint)}`, {
        method: 'DELETE'
      });
      
      this.subscription = null;
      return { success: true };
    } catch (error) {
      console.error('Push unsubscribe failed:', error);
      return { success: false, error: error.message };
    }
  }

  isSubscribed() {
    return !!this.subscription;
  }

  getPermissionStatus() {
    if (!this.isSupported) return 'unsupported';
    return Notification.permission;
  }
}

// Helper functions
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer) {
  if (!buffer) return '';
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

// Export singleton instance
export const pushService = new PushNotificationService();
export default pushService;
